import apiClient from './apiClient';

export interface VoucherLine {
  sequence: number;
  account: string;
  accountName: string;
  taxAccount: string;
  taxAccountName: string;
  side: 'D' | 'C' | string;
  amount: number;
  summary: string;
  department: string;
  departmentName?: string;
  writeOffCode1?: string;
  writeOffCode2?: string;
  temporaryVoucher?: string;
}

export interface TemporaryVoucherHeader {
  number: string;
  formal: string;
  voucherDate: string;
  sourceType: string;
  billType: string;
  billTypeName: string;
  sourceNumber: string;
  party: string;
  status: string;
  detailCount: number;
  debitTotal: number;
  creditTotal: number;
}

export interface FormalVoucherHeader {
  code: string;
  voucherDate: string;
  detailCount: number;
  debitTotal: number;
  creditTotal: number;
  status: string;
  confirmUser: string;
  confirmDate: string;
  sourceSystemVoucher: string;
}

export interface VoucherAuditEntry {
  auditId: number;
  voucherCode: string;
  action: 'CREATE' | 'UPDATE' | 'VOID' | string;
  actor: string;
  previousStatus: string;
  newStatus: string;
  occurredAt: string;
}

interface ListResult<T> { items: T[]; total: number; page: number; pageSize: number }

export interface SaveVoucherLine {
  account: string;
  accountName?: string;
  taxAccount?: string;
  taxAccountName?: string;
  side: 'D' | 'C';
  amount: number;
  summary: string;
  writeOffCode1?: string;
  writeOffCode2?: string;
  department: string;
  departmentName?: string;
}

export const chain29VoucherApi = {
  async listTemporary(status: 'unconfirmed' | 'confirmed' | 'voided' = 'unconfirmed') {
    return (await apiClient.get<ListResult<TemporaryVoucherHeader>>('/api/accounting/temp-vouchers', {
      params: { status, page: 1, pageSize: 200 },
    })).data;
  },
  async getTemporary(number: string) {
    return (await apiClient.get<{ header: TemporaryVoucherHeader; details: VoucherLine[] }>(
      `/api/accounting/temp-vouchers/${encodeURIComponent(number)}`,
    )).data;
  },
  async editTemporaryDetail(number: string, sequence: number, line: SaveVoucherLine) {
    return (await apiClient.put(`/api/accounting/temp-vouchers/${encodeURIComponent(number)}/details/${sequence}`, line)).data;
  },
  async formalizeTemporary(number: string, confirmDate: string) {
    return (await apiClient.post(`/api/accounting/temp-vouchers/${encodeURIComponent(number)}/formalize`, { confirmDate })).data;
  },
  async formalizeBatch(temporaryVouchers: string[], confirmDate: string, mergeByVoucherDate: boolean) {
    return (await apiClient.post('/api/accounting/temp-vouchers/formalize', {
      temporaryVouchers, confirmDate: mergeByVoucherDate ? '' : confirmDate, mergeByVoucherDate,
    })).data;
  },
  async voidTemporary(number: string) {
    return (await apiClient.post(`/api/accounting/temp-vouchers/${encodeURIComponent(number)}/void`, {})).data;
  },
  async listFormal(status = '') {
    return (await apiClient.get<ListResult<FormalVoucherHeader>>('/api/accounting/vouchers', {
      params: { status, page: 1, pageSize: 200 },
    })).data;
  },
  async getFormal(code: string) {
    return (await apiClient.get<{ header: FormalVoucherHeader; details: VoucherLine[] }>(
      `/api/accounting/vouchers/${encodeURIComponent(code)}`,
    )).data;
  },
  async listFormalAudit(code: string) {
    return (await apiClient.get<{ data: VoucherAuditEntry[] }>(
      `/api/accounting/vouchers/${encodeURIComponent(code)}/audit`,
    )).data.data;
  },
  async createFormal(voucherDate: string, details: SaveVoucherLine[]) {
    return (await apiClient.post('/api/accounting/vouchers', { voucherDate, details })).data;
  },
  async updateFormal(code: string, voucherDate: string, details: SaveVoucherLine[]) {
    return (await apiClient.put(`/api/accounting/vouchers/${encodeURIComponent(code)}`, { voucherDate, details })).data;
  },
  async voidFormal(code: string) {
    return (await apiClient.post(`/api/accounting/vouchers/${encodeURIComponent(code)}/void`, {})).data;
  },
};
